/// <amd-module name="Wit/Common/Controls/Boards/Draggable" />
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useIsMounted, usePageContext } from "VSS/Platform/Layout";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

/** the user must move the pointer more than this many pixels before a drag starts */
export const minDragDistance = 8;
export const minYScrollDistanceDefault = 60;
export const minXScrollDistanceDefault = 225;

export interface IPosition {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface IScrollableSettings {
    options?: ScrollToOptions;
    debounceTime?: number;
    minYScrollDistance?: number;
    minXScrollDistance?: number;
    forbiddenAreasSelectors?: string;
}

export interface ScrollableData {
    scrollableContainer?: React.RefObject<HTMLDivElement>;
    scrollableSettings?: IScrollableSettings;
}

export const enum DragMode {
    move = "move",
    resizeLeft = "resizeLeft",
    resizeRight = "resizeRight"
}

/**
 * Props a component needs to support to be Draggable.
 */
export interface IWrappedDraggableProps {
    /** true if currently being dragged */
    dragging: boolean;
    /** how the element is being dragged */
    dragMode: DragMode;
    /** if true, moving is disabled */
    isMoveDisabled?: () => boolean;
    /** current position if currently being dragged */
    position: IPosition | undefined;
    /** if true, resizing is disabled */
    resizeDisabled?: boolean;
    /** use this for the onPointerDown event handler */
    onPointerDown: (event: React.PointerEvent<HTMLElement>, mode: DragMode) => void;
}

export interface IDraggableProps<TWrappedProps> {
    /**
     * If true, moving is disabled
     */
    isMoveDisabled?: () => boolean;
    /**
     * called when the drag starts
     */
    onDragStart?: (e: PointerEvent, draggingElement: HTMLElement, mode: DragMode) => void;
    /**
     * called when the element is moved
     */
    onDrag?: (
        e: PointerEvent,
        draggingElement: HTMLElement,
        mode: DragMode,
        position: IPosition
    ) => { overridePosition: IPosition | undefined; overrideProps: Partial<TWrappedProps> } | undefined;
    /**
     * called when the drag ends
     * draggingElement is the element that was initially clicked to begin the drag.
     */
    onDragComplete?: (e: PointerEvent, draggingElement: HTMLElement, mode: DragMode, position: IPosition) => void;
    /**
     * If true, resizing is disabled
     */
    resizeDisabled?: boolean;
    /**
     * Used to scroll parent container via dragging, use default on draggable move function
     */
    scrollableData?: ScrollableData;

    /*
     * Custom on drag move implementation, used to scroll parent container via dragging
     */
    onDraggableMove?: (e: PointerEvent, scrollLeftValue: number, scrollTopValue: number) => void;
}

export function MakeDraggable<TProps>(WrappedComponent: (props: TProps & IWrappedDraggableProps) => JSX.Element) {
    function Draggable(props: TProps & IDraggableProps<TProps>) {
        // position of the component that is being dragged
        const [position, setPosition] = useState<IPosition | undefined>();
        // mouse and initial positions that we need to track
        const positionsRef = useRef({
            dragging: false,
            mode: DragMode.move,
            initialLeft: NaN,
            initialTop: NaN,
            initialScrollLeft: NaN,
            initialScrollTop: NaN,
            initialWidth: NaN,
            initialHeight: NaN,
            initialMouseLeft: NaN,
            initialMouseTop: NaN,
            lastEvent: undefined as PointerEvent | undefined,
            additionalProps: undefined as Partial<TProps> | undefined
        });
        // we should try to strip out all the IDraggableProps from wrappedProps
        const { onDragStart, onDrag, onDragComplete, onDraggableMove, scrollableData, ...wrappedProps } = props;
        const animationFrame = useRef<number | undefined>();
        const onDragStartRef = useRef(onDragStart);
        const onDragRef = useRef(onDrag);
        const selectedOnDraggableMoveRef = useRef<((e: PointerEvent, scrollLeftValue: number, scrollTopValue: number) => void) | undefined>();

        useEffect(() => {
            const timer = new TimerManagement();

            // select onDraggableMove function depends on arguments (or leave undefined)
            let selectedOnDraggableMove: ((e: PointerEvent, scrollLeftValue: number, scrollTopValue: number) => void) | undefined = undefined;
            if (onDraggableMove) {
                selectedOnDraggableMove = onDraggableMove;
            } else if (scrollableData && scrollableData.scrollableContainer) {
                selectedOnDraggableMove = onDraggableMoveDefault;
            }

            // add debounce to selected onDraggableMove function - in case debounceTime is set
            if (selectedOnDraggableMove) {
                if (scrollableData?.scrollableSettings?.debounceTime) {
                    selectedOnDraggableMoveRef.current = timer?.debounce(selectedOnDraggableMove, scrollableData.scrollableSettings.debounceTime);
                } else {
                    selectedOnDraggableMoveRef.current = selectedOnDraggableMove;
                }
            }
        }, [onDraggableMove, scrollableData, scrollableData?.scrollableSettings?.debounceTime]);

        const onDragCompleteRef = useRef(onDragComplete);
        const draggingElement = useRef<HTMLElement>();
        onDragStartRef.current = onDragStart;
        onDragRef.current = onDrag;
        onDragCompleteRef.current = onDragComplete;
        const cardsDragAndDropFixEnabled = useRef<boolean>(
            !isFeatureFlagEnabled(usePageContext(), FeatureFlags.CardsDragAndDropFixDisabled, false)
        ).current;

        const isMounted = useIsMounted();

        // purposefully using useRef() here instead of useCallback().
        // since we're using these outside of react, we need to keep handles to the exact function so that we can detach the event listener.
        // we can use references to keep our closure items up to date
        const pointerMoveHandler = useRef((e: PointerEvent) => {
            if (e.defaultPrevented) {
                // clean up and stop drag if a previous event handler has prevented default already
                setPosition(undefined);
                detachHandlers.current();
                positionsRef.current.dragging = false;
                return;
            }

            positionsRef.current.lastEvent = e;

            // don't start dragging until the user has moved a minimum distance
            if (
                !positionsRef.current.dragging &&
                (Math.abs(e.pageX - positionsRef.current.initialMouseLeft) > minDragDistance ||
                    Math.abs(e.pageY - positionsRef.current.initialMouseTop) > minDragDistance)
            ) {
                setTimeout(() => onDragStartRef.current && onDragStartRef.current(e, draggingElement.current!, positionsRef.current.mode), 0);
                positionsRef.current.dragging = true;
            }

            if (positionsRef.current.dragging) {
                e.preventDefault();

                if (selectedOnDraggableMoveRef.current) {
                    const { scrollLeft, scrollTop } = calculateCumulativeScroll(draggingElement.current?.parentElement);
                    const scrollLeftValue = scrollLeft - positionsRef.current.initialMouseLeft + e.pageX;
                    const scrollTopValue = scrollTop - positionsRef.current.initialMouseTop + e.pageY;

                    selectedOnDraggableMoveRef.current(e, scrollLeftValue, scrollTopValue);
                }

                if (animationFrame.current === undefined) {
                    animationFrame.current = requestAnimationFrame(() => {
                        animationFrame.current = undefined;
                        if (!positionsRef.current?.lastEvent || !positionsRef.current.dragging) {
                            return undefined;
                        }
                        let newPosition = calculatePosition(positionsRef.current.lastEvent);
                        if (newPosition) {
                            const overrides =
                                onDragRef.current &&
                                positionsRef.current.lastEvent &&
                                onDragRef.current(positionsRef.current.lastEvent, draggingElement.current!, positionsRef.current.mode, newPosition);
                            if (overrides?.overridePosition) {
                                newPosition = overrides.overridePosition;
                            }
                            positionsRef.current.additionalProps = overrides?.overrideProps;
                        }
                        setPosition(newPosition);
                    });
                }
            }
        });

        const pointerUpHandler = useRef((e: PointerEvent) => {
            if (positionsRef.current.dragging) {
                const newPosition = calculatePosition(e);
                onDragCompleteRef.current && onDragCompleteRef.current(e, draggingElement.current!, positionsRef.current.mode, newPosition);
                positionsRef.current.dragging = false;
            }

            if (isMounted()) {
                setPosition(undefined);
            }

            detachHandlers.current();
        });

        const detachHandlers = useRef(() => {
            document.removeEventListener("pointerup", pointerUpHandler.current);
            document.removeEventListener("pointermove", pointerMoveHandler.current);
        });

        const pointerDownHandler = useCallback((e: React.PointerEvent<HTMLElement>, mode: DragMode) => {
            if (
                e.button !== 0 ||
                e.defaultPrevented ||
                (e.target as HTMLElement).nodeName === "INPUT" ||
                (e.target as HTMLElement).nodeName === "TEXTAREA" ||
                (mode === DragMode.move && props.isMoveDisabled?.()) ||
                ((mode === DragMode.resizeLeft || mode === DragMode.resizeRight) && props.resizeDisabled)
            ) {
                return;
            }
            const element = e.currentTarget;
            draggingElement.current = element;
            const { offsetLeft, offsetTop } = calculateOverallOffset(element);
            const { scrollLeft, scrollTop } = calculateCumulativeScroll(element);
            positionsRef.current = {
                dragging: false,
                mode,
                initialLeft: offsetLeft,
                initialTop: offsetTop,
                initialScrollLeft: scrollLeft,
                initialScrollTop: scrollTop,
                initialWidth: element.offsetWidth,
                initialHeight: element.offsetHeight,
                initialMouseLeft: e.pageX,
                initialMouseTop: e.pageY,
                lastEvent: undefined,
                additionalProps: undefined
            };
            setPosition({
                left: offsetLeft,
                top: offsetTop,
                width: element.offsetWidth,
                height: element.offsetHeight
            });
            document.addEventListener("pointerup", pointerUpHandler.current);
            document.addEventListener("pointermove", pointerMoveHandler.current);
        }, []);

        useEffect(() => detachHandlers.current, []);

        return (
            <WrappedComponent
                {...(wrappedProps as any as TProps)}
                {...positionsRef.current.additionalProps}
                dragging={positionsRef.current.dragging}
                dragMode={positionsRef.current.mode}
                position={position}
                onPointerDown={pointerDownHandler}
            />
        );

        function calculatePosition(lastEvent: PointerEvent): IPosition {
            const { scrollLeft, scrollTop } = calculateCumulativeScroll(draggingElement.current?.parentElement);
            const mode = positionsRef.current.mode;
            const deltaX = lastEvent.pageX - positionsRef.current.initialMouseLeft;
            const deltaY = lastEvent.pageY - positionsRef.current.initialMouseTop;

            const scrollTopValue = mode === DragMode.move ? positionsRef.current.initialScrollTop : scrollTop;
            const scrollLeftValue = mode === DragMode.move ? positionsRef.current.initialScrollLeft : scrollLeft;

            const top =
                mode === DragMode.move ? positionsRef.current.initialTop + deltaY - scrollTopValue : positionsRef.current.initialTop - scrollTop;
            const left =
                mode === DragMode.move || mode === DragMode.resizeLeft
                    ? positionsRef.current.initialLeft + deltaX - scrollLeftValue
                    : positionsRef.current.initialLeft - scrollLeft;
                    
            let width: number | undefined;
            switch (mode) {
                case DragMode.move:
                    width = positionsRef.current.initialWidth;
                    break;
                case DragMode.resizeLeft:
                    width = positionsRef.current.initialWidth - deltaX;
                    break;
                case DragMode.resizeRight:
                    width = positionsRef.current.initialWidth + deltaX;
            }
            return {
                height: positionsRef.current.initialHeight,
                left,
                top,
                width
            };
        }

        function onDraggableMoveDefault(e: PointerEvent, scrollLeftValue: number, scrollTopValue: number): void {
            if (!scrollableData || !scrollableData.scrollableContainer || scrollableData.scrollableContainer.current === null) {
                return;
            }

            if (cardsDragAndDropFixEnabled) {
                const forbiddenArea = scrollableData.scrollableSettings?.forbiddenAreasSelectors
                    ? findElementAtPoint(scrollableData.scrollableSettings?.forbiddenAreasSelectors, e.pageX, e.pageY)
                    : undefined;

                if (forbiddenArea) {
                    return;
                }
            }

            const scrollableContainerCurrent = scrollableData.scrollableContainer.current;
            const minYScrollDistance = scrollableData.scrollableSettings?.minYScrollDistance ?? minYScrollDistanceDefault;
            const minXScrollDistance = scrollableData.scrollableSettings?.minXScrollDistance ?? minXScrollDistanceDefault;

            const heigthDifferenceForContainer = window.innerHeight - scrollableContainerCurrent.clientHeight;
            const widthDifferenceForContainer = window.innerWidth - scrollableContainerCurrent.clientWidth;

            let actualYPosition = e.pageY - heigthDifferenceForContainer;
            let actualXPosition = e.pageX - widthDifferenceForContainer;

            if (cardsDragAndDropFixEnabled) {
                const containerRect = scrollableContainerCurrent.getBoundingClientRect();
                if (containerRect) {
                    actualYPosition = e.pageY - containerRect.top;
                    actualXPosition = e.pageX - containerRect.left;
                }
            }

            const distanceToBottomBorder = scrollableContainerCurrent.clientHeight - actualYPosition;
            const distanceToRigthBorder = scrollableContainerCurrent.clientWidth - actualXPosition;

            const scrollTop = actualYPosition < minYScrollDistance;
            const scrollBottom = distanceToBottomBorder < minYScrollDistance;
            const scrollLeft = actualXPosition < minXScrollDistance;
            const scrollRight = distanceToRigthBorder < minXScrollDistance;

            if (scrollTop || scrollBottom || scrollLeft || scrollRight) {
                const options: ScrollToOptions = {
                    behavior: "smooth"
                };

                if (scrollTop || scrollBottom) {
                    options.top = scrollTopValue;
                }

                if (scrollLeft || scrollRight) {
                    options.left = scrollLeftValue;
                }

                scrollableContainerCurrent.scrollTo(options);
            }
        }
    }
    return Draggable;
}

function calculateCumulativeScroll(element: HTMLElement | null | undefined): { scrollLeft: number; scrollTop: number } {
    let scrollLeft = 0;
    let scrollTop = 0;
    for (let parent = element; !!parent; parent = parent.parentElement) {
        scrollLeft += parent.scrollLeft;
        scrollTop += parent.scrollTop;
    }
    return { scrollLeft, scrollTop };
}

function calculateOverallOffset(element: HTMLElement | null | undefined): { offsetLeft: number; offsetTop: number } {
    let offsetLeft = 0;
    let offsetTop = 0;
    for (let e = element; !!e; e = e.offsetParent as HTMLElement | null) {
        offsetLeft += e.offsetLeft;
        offsetTop += e.offsetTop;
    }
    return { offsetLeft, offsetTop };
}

export function calculateAbsolutePosition(element: HTMLElement): { left: number; top: number } {
    const scroll = calculateCumulativeScroll(element);
    const offset = calculateOverallOffset(element);
    return {
        left: offset.offsetLeft - scroll.scrollLeft,
        top: offset.offsetTop - scroll.scrollTop
    };
}

/**
 * Test to see if any element matching selector is under the point (pageX, pageY).
 * If pageX is undefined, ignore the x coordinates.
 * @param selector
 * @param pageX
 * @param pageY
 */
export function findElementAtPoint(selector: string, pageX: number | undefined, pageY: number): HTMLElement | undefined {
    const candidates = Array.from(document.querySelectorAll(selector));
    const target = candidates.find(el => {
        const rect = el.getBoundingClientRect();

        return (
            (pageX === undefined || pageX > rect.left + window.scrollX) &&
            (pageX === undefined || pageX < rect.right + window.scrollX) &&
            pageY > rect.top + window.scrollY &&
            pageY < rect.bottom + window.scrollY
        );
    });

    return target as HTMLElement | undefined;
}
